/**
 * <TutorEarningsCard> — « Mon mois · Mes paiements », le mois du masteur sur l'accueil (organism).
 *
 * L'ancienne « Mon historique » (`CoachStats.js`), rhabillée : ses heures de live et ses montants,
 * « Ce mois-ci » ou « Le mois dernier » (des onglets, `<Tabs>`, à la place du bouton qui basculait de l'un à
 * l'autre). L'intitulé est « Mon mois » (Wajih, 29/10), pas le nom du mois : il ne dit pas
 * « Octobre » au-dessus des chiffres de septembre. La ligne de dates dit de quel mois on parle.
 *
 * Deux branches, comme l'ancienne :
 *  - **compte Stripe rempli** : les heures validées et en attente, les montants validés et en
 *    attente ; « Mes paiements » ouvre le tableau Stripe (l'ancien « Suivre mes paiements »), et
 *    « Historique des lives » sa liste ;
 *  - **sans compte Stripe** : les heures validées et le montant validé cumulé, et l'astérisque
 *    quand des lives attendent encore leur relecture. Ni « Mes paiements » ni l'historique : l'ancien
 *    bouton ne se montrait qu'avec un compte rempli.
 *
 * La période choisie est de l'état d'interface : elle vit ici (`initialPeriod` pour une story).
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { List } from "../../molecules/List"
import { ReferralStatTile } from "../../molecules/ReferralStatTile"
import { Tabs } from "../../molecules/Tabs"
import {
    type UITutorEarnings,
    type UITutorEarningsPeriodKey,
} from "../../../ports/primary/ui/types/tutorCoquilleAccueil"
import { euros } from "../../../ports/primary/ui/formatters/moneyFormatter"

const PERIOD_LABELS: Record<UITutorEarningsPeriodKey, string> = {
    this_month: "Ce mois-ci",
    last_month: "Le mois dernier",
}
const PERIOD_ORDER: UITutorEarningsPeriodKey[] = ["this_month", "last_month"]

/** L'intitulé de la carte, et de son squelette. */
export const TUTOR_EARNINGS_TITLE = "Mon mois"

const NBSP = " "
const hours = (count: number) => `${count}${NBSP}h`

export type TutorEarningsCardProps = {
    earnings: UITutorEarnings
    /** « Mes paiements » : le tableau Stripe. Absent, ou sans compte Stripe : pas de lien. */
    onOpenPayments?: () => void
    /** L'adresse du tableau Stripe est demandée : le lien attend. */
    openingPayments?: boolean
    /** « Historique des lives ». Absent, ou sans compte Stripe : pas de lien. */
    onOpenLivesHistory?: () => void
    /** La période ouverte au montage — ce mois-ci par défaut. */
    initialPeriod?: UITutorEarningsPeriodKey
    testID?: string
}

export function TutorEarningsCard(props: TutorEarningsCardProps) {
    const { earnings, onOpenPayments, openingPayments = false, onOpenLivesHistory, initialPeriod, testID } = props
    const [periodKey, setPeriodKey] = useState<UITutorEarningsPeriodKey>(initialPeriod ?? "this_month")
    const period = earnings.periods[periodKey]
    const stripe = earnings.stripeConnected

    return (
        <List
            title={TUTOR_EARNINGS_TITLE}
            headerRight={
                stripe && onOpenPayments != null ? (
                    <TextButton
                        label={openingPayments ? "Redirection…" : "Mes paiements"}
                        onPress={onOpenPayments}
                        disabled={openingPayments}
                        size="sm"
                    />
                ) : undefined
            }
            testID={testID}
        >
            <Card p="4">
                <Stack gap="4">
                    <Stack gap="2">
                        <Tabs<UITutorEarningsPeriodKey>
                            size="sm"
                            items={PERIOD_ORDER.map((key) => ({ key, label: PERIOD_LABELS[key] }))}
                            value={periodKey}
                            onChange={setPeriodKey}
                            fadeColor="surface-raised"
                            accessibilityLabel="Période"
                        />
                        <Text size="text-xs" color="text-muted">
                            {period.pendingReview === true && !stripe ? `${period.rangeLabel}*` : period.rangeLabel}
                        </Text>
                    </Stack>

                    {stripe ? (
                        <View style={styles.tiles}>
                            <Tile value={hours(period.validatedHours)} label="de live validées" />
                            <Tile value={hours(period.pendingHours ?? 0)} label="de live en attente" />
                            <Tile value={euros(period.validatedAmount)} label="validés" />
                            <Tile value={euros(period.pendingAmount ?? 0)} label="en attente" />
                        </View>
                    ) : (
                        <View style={styles.tiles}>
                            <Tile value={hours(period.validatedHours)} label="de live validées" />
                            <Tile value={euros(period.validatedAmount)} label="validés au total" />
                        </View>
                    )}

                    {period.pendingReview === true && !stripe && (
                        <Text size="text-xs" color="text-muted">
                            *Certains lives en attente de validation ne sont pas encore comptés.
                        </Text>
                    )}

                    {stripe && onOpenLivesHistory != null && (
                        <Row gap="2">
                            <TextButton label="Historique des lives" onPress={onOpenLivesHistory} size="sm" />
                        </Row>
                    )}
                </Stack>
            </Card>
        </List>
    )
}

/** Un chiffre de la carte : il prend sa part de la rangée, et passe à la ligne quand elle est étroite. */
function Tile(props: { value: string; label: string }) {
    return (
        <View style={styles.tile}>
            <ReferralStatTile value={props.value} label={props.label} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    tiles: {
        flexDirection: "row",
        flexWrap: "wrap",
        rowGap: theme.spacing["3"],
        columnGap: theme.spacing["4"],
    },
    tile: {
        flexGrow: 1,
        flexBasis: "40%",
        minWidth: 110,
    },
}))
